import { CalendarGridColumnName } from '../../../grid/types/column.types';
import {
  IFilterComparator,
  ConfigureOptionsParams,
  FilterOptions,
  FilterPassParams,
  ESGFilterModel,
  AppliedFilterItem
} from '../filter-types';
import { FilterResultFacade } from '../filter-result-facade';
import { FilterHandlerBase } from './base/filter-handler-base';
import { esgValueGetter } from '../../../grid/utils/value-getters/esg';
import { ESGFilterModelComparator } from './comparators/esg-model-comparator';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../../state/calendar-filter/state';
import { excludeValues } from '../filter-utils';
import {
  FilterChange,
  FilterClear
} from '../../../state/calendar-filter/actions';

export class ESGFilterHandler extends FilterHandlerBase {
  private ESG_TYPES_LABEL = 'ESG Types';
  private ESG_VERIFIERS_LABEL = 'ESG Verifiers';
  private ESG_PRE_QALIFIED_STATUS = 'ESG Pre-Qualified Status';

  private comparator: IFilterComparator<ESGFilterModel>;

  get name(): string {
    return CalendarGridColumnName.ESG;
  }

  get isInclusiveExclusive(): boolean {
    return false;
  }

  configureFilterOptions(params: ConfigureOptionsParams): void {
    params.setFilterService.esgCertification =
      params.filterOptions.esgCertification;
  }

  getFilterOptions(filterOptions: FilterOptions): any {
    return filterOptions.esgCertification;
  }

  hasValidDiff(options: FilterOptions, grid: any, store: any): boolean {
    return this.getValidDiffComparator(options).hasValidDiff(grid, store);
  }

  getValidDiffComparator(
    options: FilterOptions
  ): IFilterComparator<ESGFilterModel> {
    if (!this.comparator) {
      this.comparator = new ESGFilterModelComparator();
    }
    this.comparator.applyFilterOptions(this.getFilterOptions(options));
    return this.comparator;
  }

  doFilterPass(params: FilterPassParams, result: FilterResultFacade) {
    const esgCertificationType =
      params.filters[this.name].model.selectedTypes.values;
    const esgCertificationVerifier =
      params.filters[this.name].model.selectedVerifiers.values;
    const esgPreQualifiedOptionSelected =
      params.filters[this.name].model.selectedPreQualified;
    const esgVals = esgValueGetter({ data: params.deal });

    const isEsgFilterPassed = !esgVals
      ? esgCertificationType.some((ea) => ea === 'Blank') &&
        esgCertificationVerifier.some((ec) => ec === 'Blank') &&
        esgPreQualifiedOptionSelected === undefined
      : !!esgCertificationType.filter((ea) =>
          esgVals
            ? esgVals.esgCertTypes.some((dealCertType) => dealCertType === ea)
            : null
        ).length &&
        !!esgCertificationVerifier.filter((ea) =>
          esgVals
            ? esgVals.esgCertVerifiers.some(
                (dealCertVerifier) => dealCertVerifier === ea
              )
            : null
        ).length &&
        (esgPreQualifiedOptionSelected === undefined ||
          (esgVals.esgPreapprovedInds &&
            esgVals.esgPreapprovedInds.includes(
              esgPreQualifiedOptionSelected
            )));

    result.applyDealFilerResult(params.deal.id, this.name, isEsgFilterPassed);
    return isEsgFilterPassed;
  }

  recalculateExclusiveValues(
    currentFilter: CalendarFilter,
    filterOptions: FilterOptions
  ) {
    const filterModel = { ...(currentFilter.model as ESGFilterModel) };
    const options: any = this.getFilterOptions(filterOptions);

    const typesModel = { ...filterModel.selectedTypes };
    if (typesModel.isInclusive) {
      typesModel.values = typesModel.storeValues ? typesModel.storeValues : [];
    } else {
      typesModel.values = excludeValues(
        options.esgCertTypes,
        typesModel.storeValues
      );
    }

    const verifiersModel = { ...filterModel.selectedVerifiers };
    if (verifiersModel.isInclusive) {
      verifiersModel.values = verifiersModel.storeValues
        ? verifiersModel.storeValues
        : [];
    } else {
      verifiersModel.values = excludeValues(
        options.esgCertVerifiers,
        verifiersModel.storeValues
      );
    }
  }

  getAppliedFiltersItems(filter: CalendarFilter): AppliedFilterItem[] {
    const activeESGTypes = filter.model.selectedTypes;
    const activeESGVerifiers = filter.model.selectedVerifiers;
    const result: AppliedFilterItem[] = [];

    const typicalItem: AppliedFilterItem = {
      name: filter.name,
      label: filter.label,
      filterLabel: undefined,
      filterValue: undefined
    };

    if (activeESGTypes) {
      const types =
        activeESGTypes.isInclusive ||
        !Object.prototype.hasOwnProperty.call(activeESGTypes, 'isInclusive')
          ? activeESGTypes.values.length
            ? `Including ${activeESGTypes.values.join(', ')}`
            : 'None'
          : activeESGTypes.storeValues.length
          ? `All Except ${activeESGTypes.storeValues.join(', ')}`
          : undefined;

      if (types !== undefined) {
        result.push({
          ...typicalItem,
          label: this.ESG_TYPES_LABEL,
          filterLabel: this.ESG_TYPES_LABEL,
          filterValue: types
        });
      }
    }
    if (activeESGVerifiers) {
      const verifiers =
        activeESGVerifiers.isInclusive ||
        !Object.prototype.hasOwnProperty.call(activeESGVerifiers, 'isInclusive')
          ? activeESGVerifiers.values.length
            ? `Including ${activeESGVerifiers.values.join(', ')}`
            : 'None'
          : activeESGVerifiers.storeValues.length
          ? `All Except ${activeESGVerifiers.storeValues.join(', ')}`
          : undefined;
      if (verifiers !== undefined) {
        result.push({
          ...typicalItem,
          label: this.ESG_VERIFIERS_LABEL,
          filterLabel: this.ESG_VERIFIERS_LABEL,
          filterValue: verifiers
        });
      }
    }

    if (filter.model.selectedPreQualified !== undefined) {
      result.push({
        ...typicalItem,
        label: this.ESG_PRE_QALIFIED_STATUS,
        filterLabel: this.ESG_PRE_QALIFIED_STATUS,
        filterValue: filter.model.selectedPreQualified
          ? 'Pre-Qualified'
          : 'No Pre-Qualified'
      });
    }

    return result;
  }

  filterClear(
    filterChange: FilterClear,
    state: CalendarFiltersState
  ): FilterChange {
    const result = { name: this.name, value: undefined };

    if (filterChange.name === this.name) {
      const filterName = filterChange.name;
      const subFilterName = filterChange.subFilterName;
      const filter = { ...state[filterName] };

      switch (subFilterName) {
        case this.ESG_TYPES_LABEL:
          filter.model.selectedTypes.values.push(
            ...filter.model.selectedTypes.storeValues
          );
          filter.model.selectedTypes.storeValues = [];
          filter.model.selectedTypes.isInclusive = false;
          filter.timestamp = Date.now();
          break;
        case this.ESG_VERIFIERS_LABEL:
          filter.model.selectedVerifiers.values.push(
            ...filter.model.selectedVerifiers.storeValues
          );
          filter.model.selectedVerifiers.storeValues = [];
          filter.model.selectedVerifiers.isInclusive = false;
          filter.timestamp = Date.now();
          break;
        case this.ESG_PRE_QALIFIED_STATUS:
          filter.model.selectedPreQualified = undefined;
          filter.timestamp = Date.now();
          break;
        default:
          filter.model.selectedTypes.storeValues = [];
          filter.model.selectedVerifiers.storeValues = [];
          filter.model.selectedPreQualified = undefined;
          break;
      }

      result.value =
        !filter.model.selectedTypes.storeValues.length &&
        !filter.model.selectedVerifiers.storeValues.length &&
        filter.model.selectedPreQualified === undefined
          ? undefined
          : filter;
    }
    return result;
  }
}
